import React, { lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Localization } from '@mui/material/locale';
import { ExperimentalAtlasThemeProvider } from '@diligentcorp/atlas-theme-mui';
import GlobalNavigator from '../components/GlobalNav/GlobalNav';
import LoadingSpinner from '../components/LoadingSpinner';
import { getI18n } from 'react-i18next';
import { useUser } from '../components/OidcProvider/OidcProvider.tsx';
import { muiLocales } from '../locales/muiLocales.ts';
import { FALLBACK_LNG } from '../locales';
import { UserProfile } from '../types';
import { getTextDirection, updateDocumentDirection } from '../helpers/rtlUtils.js';
import { AppRoutes } from '../AppConfig.ts';
import { useFeatureFlag } from '../hooks/useFeatureFlag.ts';
import { useInitScripts } from '../hooks/useInitScripts.ts';

const SecuritySettings = lazy(() => import('./security-settings/SecuritySettings.tsx'));
const AiSettings = lazy(() => import('./ai-settings/AiSettings.tsx'));
const Unauthorized = lazy(() => import('./unauthorized/Unauthorized.tsx'));

type AppThemeProviderProps = React.PropsWithChildren<{
  locales: Localization[];
  direction: 'ltr' | 'rtl';
}>;

function updateHtmlLocale(locale: string) {
  const I18n = getI18n();
  I18n.changeLanguage(locale).then((_resolve) => {
    document.documentElement.lang = locale;
    updateDocumentDirection(locale);
  });
}

function AppThemeProvider({ locales, direction, children }: AppThemeProviderProps) {
  return (
    <ExperimentalAtlasThemeProvider locales={locales} tokenMode="atlas-light" direction={direction}>
      {children}
    </ExperimentalAtlasThemeProvider>
  );
}

export default function App() {
  const { userProfile, isSessionLoading, oidcError } = useUser();
  const isAiSettingsEnabled = useFeatureFlag('aiSettings');
  const [locale, setLocale] = React.useState<Localization[]>(muiLocales[FALLBACK_LNG]);
  const [currentLocale, setCurrentLocale] = React.useState<string>(FALLBACK_LNG);

  useInitScripts(!isSessionLoading && !oidcError);

  React.useEffect(() => {
    if (userProfile) {
      const { locale } = userProfile as UserProfile;
      setLocale(muiLocales[locale]);
      setCurrentLocale(locale);

      updateHtmlLocale(locale);
    }
  }, [userProfile]);

  const documentDirection = getTextDirection(currentLocale);

  if (isSessionLoading) {
    return (
      <AppThemeProvider locales={locale} direction={documentDirection}>
        <LoadingSpinner withBackdrop withoutText />
      </AppThemeProvider>
    );
  }

  if (oidcError) {
    return (
      <AppThemeProvider locales={locale} direction={documentDirection}>
        <Unauthorized />
      </AppThemeProvider>
    );
  }

  return (
    <>
      <GlobalNavigator />
      <AppThemeProvider locales={locale} direction={documentDirection}>
        <main data-atlas-gn-app="true">
          <BrowserRouter>
            <Routes>
              <Route
                path={AppRoutes.ROOT_PATH}
                element={
                  <React.Suspense fallback={<LoadingSpinner withBackdrop withoutText />}>
                    <SecuritySettings />
                  </React.Suspense>
                }
              />
              {isAiSettingsEnabled && (
                <Route
                  path={AppRoutes.AI_SETTINGS}
                  element={
                    <React.Suspense fallback={<LoadingSpinner withBackdrop withoutText />}>
                      <AiSettings />
                    </React.Suspense>
                  }
                />
              )}
              <Route path={AppRoutes.UNAUTHORIZED} element={<Unauthorized />} />,
              <Route path={AppRoutes.OIDC_CALLBACK} element={<LoadingSpinner withBackdrop withoutText />} />,
              <Route path={AppRoutes.LOGOUT_CALLBACK} element={<LoadingSpinner withBackdrop withoutText />} />,
              <Route path={AppRoutes.LOGIN_CALLBACK} element={<LoadingSpinner withBackdrop withoutText />} />,
              <Route path="*" element={<Navigate to={AppRoutes.ROOT_PATH} replace />} />,
            </Routes>
          </BrowserRouter>
        </main>
      </AppThemeProvider>
    </>
  );
}
